import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, FormControlLabel } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, HourglassBottom as HourglassBottomIcon, Message as MessageIcon } from '@mui/icons-material';

import { IUserItem, IInvitedUserItem } from "~/providers/client-users-list";
import { useTrans } from '~/hooks/useTrans';
import { IUser, IUserAdmin, TUserKeyRole } from "~/providers/interfaces";
import { IClientProjectInvitation } from "~/providers/client-interfaces";
import Loader from '~/components/Loader';
import { MySwitch } from "~/mui/MySwitch";
import { getLink } from "~/providers/route";
import { Link } from "react-router-dom";

interface IUsersTable {
	isInternal: boolean;
	isVisitor: boolean;
	loading: boolean;
	email: string;
	ownerEmail: string;
	users: Array<IUserItem>;
	invitedUsers: Array<IInvitedUserItem>;
	onRoleUpdate: (user: IUser) => void;
	onNotificationUpdate: (rusId: number, checked: boolean) => void;
	onRemoveUser: (user: IUser) => void;
	onUserDataEdit: (user: IUserAdmin) => void;
	onRemoveInvitation: (invitation: IClientProjectInvitation) => void;
	onResendInvitation: (email: string, role: TUserKeyRole) => void;
}

export default function UsersTable({
	isInternal = false,
	isVisitor = true,
	loading = true,
	email = "",
	ownerEmail = "",
	users = [],
	invitedUsers = [],
	onRoleUpdate = () => {},
	onNotificationUpdate = () => {},
	onRemoveUser = () => {},
	onUserDataEdit = () => {},
	onRemoveInvitation = () => {},
	onResendInvitation = () => {},
}: IUsersTable) {
	const { trans } = useTrans();
	const HEADERS = [
		"usersPage.tableUser",
		"usersPage.tableNotification",
		"usersPage.tableState",
		"userRole.role",
		"adminInternalUsersPage.table.createdAt",
		...isInternal
			? ["usersTable.admin.maxProjectCount", "usersTable.admin.banned"]
			: [],
		"",
	];

	// tohle pouze pro klienty
	function showResendBtn(invitedUserItem: IInvitedUserItem): boolean {
		return !isVisitor && !isInternal && (invitedUserItem.data.refused || invitedUserItem.data.expired);
	}

	function showUserEdit(userItem: IUserItem) {
		return (isInternal && userItem.data.role !== "owner") || ((!isVisitor && userItem.data.role !== "owner") && (userItem.email !== email && userItem.email !== ownerEmail));
	}

	function showUserDelete(userItem: IUserItem) {
		return isInternal || (!isVisitor && userItem.data.role !== "owner");
	}

	function getEmailLink(keyword: string) {
		return getLink("admin-projects", true, { keyword });
	}

	return <TableContainer component={Paper} sx={{ marginTop: "25px" }}>
		<Table sx={{ minWidth: 650 }} aria-label="table">
			<TableHead>
				<TableRow>
					{ HEADERS.map(header => <TableCell sx={{ fontWeight: "bold" }} key={header}>
						{ header && trans(header) }
					</TableCell>) }
				</TableRow>
			</TableHead>
			<TableBody>
				{ users.map(userItem => (<TableRow key={userItem.email}>
					<TableCell>
						{ isInternal && <Link to={getEmailLink(userItem.email)}>
							{ userItem.email }
						</Link> }
						{ !isInternal && userItem.email }
					</TableCell>
					<TableCell>
						<FormControlLabel
							control={<MySwitch sx={{ m: 1 }} checked={userItem.notification} onChange={event => onNotificationUpdate(userItem.data.id, event.target.checked)} />}
							label="" sx={{ margin: 0 }} disabled={isVisitor}
						/>
					</TableCell>
					<TableCell>{ userItem.state }</TableCell>
					<TableCell>{ userItem.role }</TableCell>
					<TableCell>{ userItem.createdAt }</TableCell>
					{ isInternal && <>
						<TableCell>
							{ (userItem.data as IUserAdmin).maxProjectCount }
						</TableCell>
						<TableCell>
							{ (userItem.data as IUserAdmin).banned ? trans("banned.yes") : trans("banned.no") }
						</TableCell>
					</> }
					<TableCell>
						<div style={{ display: "flex", gap: "var(--margin-small)", justifyContent: "center", alignItems: "center", minHeight: "36px" }}>
							{ showUserEdit(userItem) && <Button color="success" onClick={() => onRoleUpdate(userItem.data)}>
								<EditIcon />
							</Button> }
							{ isInternal && <Button color="success" onClick={() => onUserDataEdit(userItem.data as IUserAdmin)}>
								<MessageIcon />
							</Button> }
							{ showUserDelete(userItem) && <Button color="success" onClick={() => onRemoveUser(userItem.data)}>
								<DeleteIcon />
							</Button> }
						</div>
					</TableCell>
				</TableRow>)) }
				{ invitedUsers.map(invitedUser => (<TableRow key={invitedUser.email}>
					<TableCell>{ invitedUser.email }</TableCell>
					<TableCell></TableCell>
					<TableCell>{ invitedUser.state }</TableCell>
					<TableCell>{ invitedUser.role }</TableCell>
					<TableCell>{ invitedUser.createdAt }</TableCell>
					<TableCell></TableCell>
					<TableCell></TableCell>
					<TableCell>
						<div style={{ display: "flex", gap: "var(--margin-small)", justifyContent: "center", alignItems: "center" }}>
							{ !invitedUser.data.expired && !invitedUser.data.refused && <Tooltip title={trans("usersPage.waitingState")}>
								<HourglassBottomIcon sx={{ fill: "var(--dark-green-text-color)" }} />
							</Tooltip> }
							<Button color="success" onClick={() => onRemoveInvitation(invitedUser.data)}>
								<DeleteIcon />
							</Button>
							{ showResendBtn(invitedUser) && <Button sx={{ marginLeft: "var(--margin-small)" }} color="success" variant="contained" onClick={() => onResendInvitation(invitedUser.data.invitedEmail, invitedUser.data.invitedRole)}>
								{ trans("usersPage.btnResendInvitation") }
							</Button> }
						</div>
					</TableCell>
				</TableRow>)) }
				{ (users.length === 0 && invitedUsers.length === 0) && <TableRow>
					<TableCell colSpan={HEADERS.length} className="text-align-center">
						{ loading && <Loader msg={trans("loadingData")} /> }
						{ !loading && trans("emptyTable") }
					</TableCell>
				</TableRow> }
			</TableBody>
		</Table>
	</TableContainer>;
}
